import type { ReactNode } from 'react';
import type { ItemDisplayColumn } from '@/api/hanly-v2/hanlyAPIV2.schemas';
import { Button } from '@/components/ui/button';
import { useShowPrices } from '@/contexts/auth';
import { useIsDesktop } from '@/hooks/use-is-desktop';
import { DeliveryDateSection } from './delivery-date-section';
import { formatYen } from './item-display';
import { NotesSection } from './notes-section';
import { type ItemFilter, OrderItemsEditor } from './order-items-editor';
import { OrderTotals } from './order-totals';
import type { OrderItemDisplayData, OrderTotalsData } from './types';

interface OrderFormBodyProps {
  items: OrderItemDisplayData[];
  itemDisplayColumns?: readonly ItemDisplayColumn[];
  totals: OrderTotalsData;
  onConfirmClick: () => void;
  emptyMessage: string;
  initialFilter?: ItemFilter;
  /** 明細の上に出すお知らせ(再注文でコピーした旨など) */
  notice?: ReactNode;
}

/**
 * 新規発注・発注変更で共通の入力画面。
 * PC は右カラムに希望納期・合計・備考・確認ボタンを固定し、スマホは画面下部に合計と確認ボタンを固定する。
 */
export const OrderFormBody = ({
  items,
  itemDisplayColumns,
  totals,
  onConfirmClick,
  emptyMessage,
  initialFilter,
  notice,
}: OrderFormBodyProps) => {
  const isDesktop = useIsDesktop();
  const showPrices = useShowPrices();
  const selectedItems = items.filter((item) => item.quantity > 0);
  const editor = (
    <OrderItemsEditor
      items={items}
      itemDisplayColumns={itemDisplayColumns}
      emptyMessage={emptyMessage}
      initialFilter={initialFilter}
    />
  );

  if (isDesktop) {
    return (
      <div className="grid grid-cols-[minmax(0,1fr)_20rem] items-start gap-6">
        <div className="flex min-w-0 flex-col gap-4">
          {notice}
          {editor}
        </div>
        <aside className="sticky top-4 flex max-h-[calc(100vh-2rem)] flex-col gap-4 overflow-y-auto border border-neutral-200 bg-white p-4">
          <DeliveryDateSection />
          <section className="flex flex-col gap-1 border-t border-neutral-200 pt-3 text-sm">
            <h3 className="text-base">選択中の商品（{selectedItems.length}品目）</h3>
            {selectedItems.length === 0 ? (
              <p className="text-neutral-600">数量を入力してください</p>
            ) : (
              <ul className="flex max-h-48 flex-col gap-1 overflow-y-auto">
                {selectedItems.map((item) => (
                  <li key={item.key} className="flex justify-between gap-3">
                    {showPrices ? (
                      <>
                        <span className="truncate">
                          {item.name} × {item.quantity.toLocaleString()}
                        </span>
                        <span className="whitespace-nowrap">{formatYen(item.amount)}</span>
                      </>
                    ) : (
                      <>
                        <span className="truncate">{item.name}</span>
                        <span className="whitespace-nowrap">
                          {item.quantity.toLocaleString()}
                          {item.unitLabel ?? ''}
                        </span>
                      </>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>
          {showPrices && (
            <div className="border-t border-neutral-200 pt-3">
              <OrderTotals totals={totals} />
            </div>
          )}
          <NotesSection />
          <Button type="button" size="lg" fullWidth onClick={onConfirmClick}>
            注文内容を確認
          </Button>
        </aside>
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col gap-4 pb-28">
        {notice}
        <section className="border border-neutral-200 bg-white p-4">
          <DeliveryDateSection />
        </section>
        {editor}
        <section className="border border-neutral-200 bg-white p-4">
          <NotesSection />
        </section>
      </div>
      <div className="fixed inset-x-0 bottom-0 z-20 flex items-center gap-3 border-t border-neutral-200 bg-white px-4 pt-2.5 pb-[max(0.75rem,env(safe-area-inset-bottom))] shadow-[0_-4px_12px_rgba(0,0,0,0.05)]">
        {showPrices ? (
          <div className="flex flex-1 flex-col leading-tight">
            <span className="text-sm text-neutral-600">{selectedItems.length}品目 ・ 合計（税込）</span>
            <span className="text-xl font-bold">¥{totals.totalAmount.toLocaleString()}</span>
          </div>
        ) : (
          <div className="flex flex-1 flex-col leading-tight">
            <span className="text-sm text-neutral-600">選択中の商品</span>
            <span className="text-xl font-bold">{selectedItems.length}品目</span>
          </div>
        )}
        <Button type="button" size="lg" onClick={onConfirmClick}>
          注文内容を確認
        </Button>
      </div>
    </>
  );
};
